import React, { useState, useEffect, useContext } from "react";
import { FaSearch, FaMicrophone } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { SearchContext } from "../../context/SearchContext"; // adjust path if needed

function SearchBar() {
  const [query, setLocalQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [placeholder, setPlaceholder] = useState("");
  const navigate = useNavigate();
  const { setQuery } = useContext(SearchContext); // context setter

  const placeholderTexts = [
    "Search For Books",
    "Search For Ebooks",
    "Search For Video Courses",
    "Search For Test Passes",
  ];

  useEffect(() => {
    let currentIndex = 0;
    let charIndex = 0;
    let typingDirection = 1;
    const typewriterInterval = setInterval(() => {
      const currentText = placeholderTexts[currentIndex];
      if (typingDirection === 1 && charIndex <= currentText.length) {
        setPlaceholder(currentText.slice(0, charIndex));
        charIndex++;
      } else if (typingDirection === -1 && charIndex >= 0) {
        setPlaceholder(currentText.slice(0, charIndex));
        charIndex--;
      }

      if (charIndex === currentText.length && typingDirection === 1) {
        typingDirection = -1;
      } else if (charIndex === 0 && typingDirection === -1) {
        typingDirection = 1;
        currentIndex = (currentIndex + 1) % placeholderTexts.length;
      }
    }, 150);

    return () => clearInterval(typewriterInterval);
  }, []);

  const fetchSuggestions = async (q) => {
    if (!q.trim()) return setSuggestions([]);
    try {
      const res = await fetch(
        `https://auth.ssccglpinnacle.com/api/search-suggestions?query=${encodeURIComponent(
          q
        )}&previousSearches=ssc,rail`
      );
      const data = await res.json();
      setSuggestions(data);
    } catch (err) {
      console.error("Error fetching suggestions:", err);
    }
  };

  const saveSearchQuery = async (q) => {
    try {
      await fetch("https://auth.ssccglpinnacle.com/api/save-search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: q }),
      });
    } catch (err) {
      console.error("Error saving query:", err);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocalQuery(value);
    fetchSuggestions(value);
    setShowSuggestions(true);
  };

  const handleSearch = async () => {
    if (query.trim()) {
      setQuery(query); // set context
      await saveSearchQuery(query);
      navigate(`/global-search?query=${encodeURIComponent(query)}`);
    }
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.title); // context
    setLocalQuery(suggestion.title); // input field
    saveSearchQuery(suggestion.title);
    setShowSuggestions(false);
    navigate(`/global-search?query=${encodeURIComponent(suggestion.title)}`);
  };

  const handleKeyPress = async (e) => {
    if (e.key === "Enter") {
      await handleSearch();
    }
  };

  const startListening = () => {
    if (!("webkitSpeechRecognition" in window)) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }

    const SpeechRecognition =
      window.webkitSpeechRecognition || window.SpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;

    recognition.start();
    setIsListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setLocalQuery(transcript);
      setQuery(transcript);
      fetchSuggestions(transcript);
      setIsListening(false);
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
  };

  return (
    <div className="relative w-full max-w-xl mx-auto">
      <div className="flex items-center border border-gray-300 rounded-md bg-white px-3 py-2 shadow-sm">
        <input
          type="text"
          className="flex-grow outline-none text-sm px-2 text-black placeholder-gray-400"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            handleInputChange(e);
            setQuery(e.target.value); // keep context in sync
          }}
          onKeyDown={handleKeyPress}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        />
        <button
          onClick={handleSearch}
          className="text-gray-600 hover:text-black ml-2"
        >
          <FaSearch />
        </button>
        <button
          onClick={startListening}
          className="text-gray-600 hover:text-red-500 ml-2"
        >
          <FaMicrophone
            className={isListening ? "animate-pulse text-red-500" : ""}
          />
        </button>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-10 w-full bg-white border border-t-0 border-gray-300 rounded-b-md max-h-60 overflow-y-auto mt-1 shadow-md">
          {suggestions.map((s, i) => (
            <li
              key={i}
              className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleSuggestionClick(s)}
            >
              <img
                src={
                  s.image ||
                  (s.type === "ebook"
                    ? "https://d3m4h509gttb94.cloudfront.net/Image/ebookImage.svg"
                    : s.type === "book"
                    ? "https://d3m4h509gttb94.cloudfront.net/Image/booksImage.svg"
                    : s.type === "video"
                    ? "https://d3m4h509gttb94.cloudfront.net/Image/video-course-Image.svg"
                    : "https://d3m4h509gttb94.cloudfront.net/Image/sampleSvgimage.svg")
                }
                alt={s.title}
                className="w-8 h-8 mr-3 object-contain"
              />
              <div>
                <p className="text-sm font-medium text-gray-800">{s.title}</p>
                <p className="text-xs text-gray-500">({s.type})</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SearchBar;
